import {
  grayColor,
  primaryColor,
  infoColor,
  successColor,
  warningColor,
  dangerColor,
  roseColor,
  whiteColor,
} from '../../material-dashboard-react';

// Shared look for the dashboard's RegularButton (components/CustomButtons).
// Matches the MUI contained button styled in the theme (src/screens/App.js):
// 10px corners, sentence case, semibold, soft shadow; primary uses the
// brand navy gradient. Change it here and every RegularButton follows.
const BRAND = {
  navy: '#2E3B6E',
  navyDark: '#1B2547',
  navyLight: '#5A69A6',
};
const SHADOW = '0 1px 2px rgba(27, 37, 71, 0.16)';
const SHADOW_HOVER = '0 3px 8px rgba(27, 37, 71, 0.22)';

// Solid colour variant with the shared shadows.
const solid = (color, hoverColor = color) => ({
  backgroundColor: color,
  boxShadow: SHADOW,
  '&:hover,&:focus': {
    backgroundColor: hoverColor,
    boxShadow: SHADOW_HOVER,
  },
});

const buttonStyle = {
  button: {
    minHeight: 'auto',
    minWidth: 'auto',
    backgroundColor: grayColor[0],
    color: whiteColor,
    boxShadow: SHADOW,
    border: 'none',
    borderRadius: '10px',
    position: 'relative',
    padding: '8px 16px',
    margin: '.3125rem 1px',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.01em',
    willChange: 'box-shadow, transform',
    transition:
      'box-shadow 0.15s ease, background 0.15s ease, background-color 0.15s ease',
    lineHeight: '1.75',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    touchAction: 'manipulation',
    cursor: 'pointer',
    '&:hover,&:focus': {
      color: whiteColor,
      backgroundColor: grayColor[0],
      boxShadow: SHADOW_HOVER,
    },
    '& .fab,& .fas,& .far,& .fal, &.material-icons': {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      marginTop: '-1em',
      marginBottom: '-1em',
      fontSize: '1.1rem',
      marginRight: '4px',
      verticalAlign: 'middle',
    },
    '& svg': {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      width: '18px',
      height: '18px',
      marginRight: '6px',
      verticalAlign: 'middle',
    },
    '&$justIcon': {
      '& .fab,& .fas,& .far,& .fal,& .material-icons': {
        marginTop: '0px',
        position: 'absolute',
        width: '100%',
        transform: 'none',
        left: '0px',
        top: '0px',
        height: '100%',
        lineHeight: '41px',
        fontSize: '20px',
      },
    },
  },
  white: {
    '&,&:focus,&:hover': {
      backgroundColor: whiteColor,
      color: grayColor[0],
    },
  },
  rose: solid(roseColor[0], roseColor[2]),
  primary: {
    background: `linear-gradient(180deg, ${BRAND.navyLight} 0%, ${BRAND.navy} 100%)`,
    boxShadow: SHADOW,
    '&:hover,&:focus': {
      background: BRAND.navyDark,
      boxShadow: SHADOW_HOVER,
    },
  },
  info: solid(infoColor[0], infoColor[2]),
  success: solid(successColor[0], successColor[2]),
  warning: solid(warningColor[0], warningColor[2]),
  danger: solid(dangerColor[0], dangerColor[2]),
  simple: {
    '&,&:focus,&:hover': {
      color: whiteColor,
      background: 'transparent',
      boxShadow: 'none',
    },
    '&$rose': {
      '&,&:focus,&:hover,&:visited': {
        color: roseColor[0],
      },
    },
    '&$primary': {
      '&,&:focus,&:hover,&:visited': {
        color: primaryColor[0],
      },
    },
    '&$info': {
      '&,&:focus,&:hover,&:visited': {
        color: infoColor[0],
      },
    },
    '&$success': {
      '&,&:focus,&:hover,&:visited': {
        color: successColor[0],
      },
    },
    '&$warning': {
      '&,&:focus,&:hover,&:visited': {
        color: warningColor[0],
      },
    },
    '&$danger': {
      '&,&:focus,&:hover,&:visited': {
        color: dangerColor[0],
      },
    },
  },
  transparent: {
    '&,&:focus,&:hover': {
      color: 'inherit',
      background: 'transparent',
      boxShadow: 'none',
    },
  },
  disabled: {
    opacity: '0.65',
    pointerEvents: 'none',
  },
  lg: {
    padding: '12px 24px',
    fontSize: '15px',
    lineHeight: '1.5',
  },
  sm: {
    padding: '6px 14px',
    fontSize: '13px',
    lineHeight: '1.5',
  },
  round: {
    borderRadius: '30px',
  },
  block: {
    width: '100% !important',
  },
  link: {
    '&,&:hover,&:focus': {
      backgroundColor: 'transparent',
      color: grayColor[0],
      boxShadow: 'none',
    },
  },
  justIcon: {
    paddingLeft: '12px',
    paddingRight: '12px',
    fontSize: '20px',
    height: '41px',
    minWidth: '41px',
    width: '41px',
    '& .fab,& .fas,& .far,& .fal,& svg,& .material-icons': {
      marginRight: '0px',
    },
    '&$lg': {
      height: '57px',
      minWidth: '57px',
      width: '57px',
      lineHeight: '56px',
      '& .fab,& .fas,& .far,& .fal,& .material-icons': {
        fontSize: '32px',
        lineHeight: '56px',
      },
      '& svg': {
        width: '32px',
        height: '32px',
      },
    },
    '&$sm': {
      height: '30px',
      minWidth: '30px',
      width: '30px',
      '& .fab,& .fas,& .far,& .fal,& .material-icons': {
        fontSize: '17px',
        lineHeight: '29px',
      },
      '& svg': {
        width: '17px',
        height: '17px',
      },
    },
  },
};

export default buttonStyle;
